import {
  Box,
  Input,
  Text,
  Textarea,
  FormLabel,
  Select,
  useDisclosure,
  useToast,
  SimpleGrid,
  Accordion,
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { getData } from 'utils/httpClient';
import authService from 'services/authService';
import VehicleHistoryModalComplete from '../Vehicles/VehicleHistoryModalComplete';
import SchedulerServiceHero from './SchedulerServiceHero';
import SchedulerCollapsibleSection from './SchedulerCollapsibleSection';

const SchedulerReceptionModalForm = ({
  selectedEvent,
  setSelectedEvent,
  vehicleService,
  customerObservations,
  setCustomerObservations,
  serviceObservations,
  setServiceObservations,
  volkswagenCost,
  setVolkswagenCost,
  hasAlerts,
  onOpenAlerts,
  showAssociateLastReception = false,
  onAssociateLastReception,
  associatingLastReception = false,
  assistant,
  setAssistant,
  mechanicsList = [],
  onMechanicChange,
  readOnly = false,
}) => {
  const toast = useToast();
  const {
    isOpen: isVehicleHistoryModalOpen,
    onOpen: onVehicleHistoryModalOpen,
    onClose: onVehicleHistoryModalClose,
  } = useDisclosure();
  const [vehicleData, setVehicleData] = useState(null);
  const [loadingVehicle, setLoadingVehicle] = useState(false);
  const [customerTransferList, setCustomerTransferList] = useState([]);

  useEffect(() => {
    const loadTransfers = async () => {
      try {
        const response = await getData('/customerTransfer', { skipLoader: true });
        setCustomerTransferList(
          response?.payload?.customerTransfers ||
            response?.payload?.customerTransfer ||
            [],
        );
      } catch (e) {
        console.error(e);
      }
    };
    loadTransfers();
  }, []);

  const fetchVehicleData = async (vehicleId) => {
    try {
      setLoadingVehicle(true);
      const response = await getData(`/vehicles/${vehicleId}`, { skipLoader: true });
      setVehicleData(response?.payload?.vehicle || null);
      onVehicleHistoryModalOpen();
    } catch {
      toast({
        title: 'Error al obtener datos del vehículo',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingVehicle(false);
    }
  };

  const appointment = selectedEvent?.appointment;

  return (
    <>
      <SchedulerServiceHero
        appointment={appointment}
        vehicleService={vehicleService}
        start={selectedEvent?.start}
        end={selectedEvent?.end}
        onOpenHistory={() => fetchVehicleData(appointment?.vehicle?.id)}
        onOpenAlerts={onOpenAlerts}
        hasAlerts={hasAlerts}
        loadingHistory={loadingVehicle}
        showAssociateLastReception={showAssociateLastReception && !readOnly}
        onAssociateLastReception={onAssociateLastReception}
        associatingLastReception={associatingLastReception}
        assistant={assistant}
        setAssistant={setAssistant}
        mechanicsList={mechanicsList}
        mechanicId={appointment?.mechanicId || vehicleService?.mechanicId}
        onMechanicChange={onMechanicChange}
        showStaffEditor={authService.isAdmin() && !readOnly}
        assistantName={
          mechanicsList?.find((m) => Number(m.id) === Number(assistant))
            ?.name || vehicleService?.assistant?.name
        }
      />

      <Box mb={4}>
        <Text fontSize="sm" fontWeight="semibold" mb={2} color="gray.700">
          Petición del cliente
        </Text>
        <Textarea
          value={customerObservations}
          onChange={(e) => !readOnly && setCustomerObservations(e.target.value)}
          placeholder="Motivo de visita…"
          size="sm"
          minH="72px"
          isReadOnly={readOnly}
        />
      </Box>

      <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4} mb={4}>
        <Box>
          <FormLabel fontSize="xs" mb={1}>
            Kilometraje actual
          </FormLabel>
          <Input
            size="md"
            value={appointment?.vehicle?.kilometers ?? ''}
            isReadOnly={readOnly}
            onChange={(e) => {
              if (readOnly) return;
              const newKilometers = e.target.value;
              setSelectedEvent((prev) => ({
                ...prev,
                appointment: {
                  ...prev.appointment,
                  vehicle: { ...prev.appointment.vehicle, kilometers: newKilometers },
                },
              }));
            }}
          />
        </Box>
        <Box>
          <FormLabel fontSize="xs" mb={1}>
            Tipo de traslado
          </FormLabel>
          <Select
            size="md"
            value={appointment?.customerTransferId || ''}
            isDisabled={readOnly}
            onChange={(e) => {
              if (readOnly) return;
              const id = Number(e.target.value);
              setSelectedEvent((prev) => ({
                ...prev,
                appointment: {
                  ...prev.appointment,
                  customerTransferId: id,
                  customerTransfer: customerTransferList.find((t) => t.id === id),
                },
              }));
            }}
          >
            {customerTransferList.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Select>
        </Box>
        <Box>
          <FormLabel fontSize="xs" mb={1}>
            Presupuesto estimado
          </FormLabel>
          <Input
            size="md"
            type="number"
            step="0.01"
            min="0"
            value={volkswagenCost ?? ''}
            isReadOnly={readOnly}
            onChange={(e) => !readOnly && setVolkswagenCost?.(e.target.value)}
            placeholder="0.00"
          />
        </Box>
      </SimpleGrid>

      <Accordion allowMultiple defaultIndex={[0]} reduceMotion>
        <SchedulerCollapsibleSection title="Observaciones de recepción">
          <FormLabel fontSize="xs" mb={1}>
            Observaciones
          </FormLabel>
          <Textarea
            size="sm"
            minH="88px"
            resize="vertical"
            value={serviceObservations || ''}
            isReadOnly={readOnly}
            onChange={(e) => !readOnly && setServiceObservations?.(e.target.value)}
            placeholder="Estado del vehículo, daños visibles, accesorios, etc."
          />
        </SchedulerCollapsibleSection>
      </Accordion>

      <VehicleHistoryModalComplete
        isOpen={isVehicleHistoryModalOpen}
        onClose={onVehicleHistoryModalClose}
        vehicleData={vehicleData}
        isLoading={loadingVehicle}
      />
    </>
  );
};

export default SchedulerReceptionModalForm;
